/**
 * DataGrid Component - Spreadsheet-style table view for data preview
 * 
 * Displays data in an Excel-like grid format with proper formatting
 */

import './DataGrid.css';

/**
 * Extract the display value from potentially nested formatted data
 * The formatting engine may return objects like { raw: "...", formatted: "...", _format_type: "..." }
 */
const getDisplayValue = (value) => {
    if (value === null || value === undefined) {
        return '';
    }

    // If it's a formatted object, extract the formatted value
    if (typeof value === 'object' && !Array.isArray(value)) {
        // Check for nested 'raw' structure from format engine
        if (value.formatted !== undefined) {
            return String(value.formatted);
        }
        if (value.raw !== undefined) {
            // Recursively handle nested raw objects
            return getDisplayValue(value.raw);
        }
        // For other objects, stringify them
        return JSON.stringify(value);
    }

    return String(value);
};

/**
 * Flatten a row that may have nested formatted objects
 */
const flattenRow = (row) => {
    if (typeof row !== 'object' || row === null) {
        return row;
    }

    const flattened = {};
    for (const [key, value] of Object.entries(row)) {
        flattened[key] = getDisplayValue(value);
    }
    return flattened;
};

const DataGrid = ({ data, title, emptyMessage = 'No data to display' }) => {
    // Handle empty or invalid data
    if (!data) {
        return (
            <div className="data-grid-empty">
                <span>{emptyMessage}</span>
            </div>
        );
    }

    // Normalize data to array format
    let rows = Array.isArray(data) ? data : [data];

    if (rows.length === 0) {
        return (
            <div className="data-grid-empty">
                <span>{emptyMessage}</span>
            </div>
        );
    }

    // Flatten all rows to handle formatted objects
    rows = rows.map(flattenRow);

    // Extract column headers from the first row
    const columns = Object.keys(rows[0] || {});

    if (columns.length === 0) {
        return (
            <div className="data-grid-empty">
                <span>{emptyMessage}</span>
            </div>
        );
    }

    return (
        <div className="data-grid-container">
            {title && <div className="data-grid-title">{title}</div>}
            <div className="data-grid-wrapper">
                <table className="data-grid">
                    <thead>
                        <tr>
                            <th className="row-number">#</th>
                            {columns.map((col, idx) => (
                                <th key={idx} className="data-grid-header">
                                    {col}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, rowIdx) => (
                            <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'even-row' : 'odd-row'}>
                                <td className="row-number">{rowIdx + 1}</td>
                                {columns.map((col, colIdx) => (
                                    <td key={colIdx} className="data-grid-cell">
                                        {row[col] !== undefined ? row[col] : ''}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default DataGrid;
